import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { useMutation } from "@tanstack/react-query";
import { axiosPrivate } from "@/api/axios";
import { cn } from "@/lib/utils";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  FileText,
  Users,
  Tag,
  Zap,
  MessageSquare,
  Shield,
  Save,
  Eye,
  Send,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import {
  SENDER_ROLE_LABELS,
  RECEIVER_ROLE_LABELS,
  MEMBERSHIP_STATE_LABELS,
  PRIMARY_SUBJECT_LABELS,
  SECONDARY_TAG_LABELS,
  TRIGGER_TYPE_LABELS,
  EVENT_TRIGGER_LABELS,
  ACTION_TRIGGER_LABELS,
  TONE_LABELS,
  SEND_DIRECTION_LABELS,
  FREQUENCY_LIMIT_LABELS,
  VISIBILITY_LABELS,
  MESSAGE_PLACEHOLDERS,
  type SenderRole,
  type ReceiverRole,
  type MembershipState,
  type PrimarySubject,
  type SecondaryTag,
  type TriggerType,
  type EventTrigger,
  type ActionTrigger,
  type ToneType,
  type SendDirection,
  type FrequencyLimit,
  type VisibilityType,
} from "./types";

// Form validation schema
const messageFormSchema = z.object({
  // Basic Metadata
  title: z.string().min(1, "Message title is required"),
  status: z.enum(["draft", "active", "archived"]),
  version_notes: z.string().optional(),

  // Role & Membership Targeting
  sender_role: z.enum(["customer", "guide", "builder", "mentor", "steward"]),
  receiver_role: z.enum(["customer", "guide", "builder", "mentor", "steward", "admin"]),
  membership_states: z.array(z.enum(["genes_only", "dxn_new", "dxn_active", "dxn_dormant"])),

  // Message Category
  primary_subject: z.enum([
    "orientation",
    "wellness_product",
    "dxn_understanding",
    "process_guidance",
    "ethics_boundaries",
    "encouragement",
    "follow_up",
    "support_help",
  ]),
  secondary_tags: z.array(
    z.enum(["informational", "optional", "time_sensitive", "clarification", "reminder"])
  ),

  // Trigger Definition
  trigger_type: z.enum(["event", "action", "time"]),
  event_trigger: z.enum([
    "orientation_completed",
    "new_dxn_member_marked",
    "prospect_added",
    "prospect_converted",
    "first_order_completed",
    "inactivity_flag",
  ]).optional(),
  action_trigger: z.enum([
    "user_clicks_follow_up",
    "user_opens_help_needed",
    "user_views_dxn_new",
    "user_selects_person_card",
    "user_opens_downline_view",
  ]).optional(),
  time_trigger_days: z.number().min(1).optional(),

  // Message Content
  message_body: z.string().min(1, "Message body is required"),
  tone: z.enum(["neutral", "supportive", "informative", "cautionary"]),

  // Delivery Rules
  send_direction: z.enum(["upline_to_downline", "downline_to_upline"]),
  frequency_limit: z.enum(["once_per_event", "once_per_person", "unlimited"]),
  visibility: z.enum(["manual_send_only", "suggested"]),
});

type MessageFormValues = z.infer<typeof messageFormSchema>;

// Section tabs configuration
const SECTIONS = [
  { id: "metadata", label: "Basic Metadata", icon: FileText },
  { id: "targeting", label: "Role & Membership", icon: Users },
  { id: "category", label: "Category", icon: Tag },
  { id: "trigger", label: "Trigger", icon: Zap },
  { id: "content", label: "Content", icon: MessageSquare },
  { id: "delivery", label: "Delivery Rules", icon: Shield },
] as const;

type SectionId = (typeof SECTIONS)[number]["id"];

export default function CreateMessagePage() {
  const [activeSection, setActiveSection] = React.useState<SectionId>("metadata");
  const [previewRole, setPreviewRole] = React.useState<ReceiverRole | null>(null);

  const form = useForm<MessageFormValues>({
    resolver: zodResolver(messageFormSchema),
    defaultValues: {
      title: "",
      status: "draft",
      version_notes: "",
      sender_role: "guide",
      receiver_role: "customer",
      membership_states: [],
      primary_subject: "orientation",
      secondary_tags: [],
      trigger_type: "event",
      event_trigger: undefined,
      action_trigger: undefined,
      time_trigger_days: undefined,
      message_body: "",
      tone: "neutral",
      send_direction: "upline_to_downline",
      frequency_limit: "once_per_event",
      visibility: "suggested",
    },
    mode: "onChange",
  });

  const triggerType = form.watch("trigger_type");

  const saveDraftMutation = useMutation({
    mutationFn: async (data: MessageFormValues) => {
      const res = await axiosPrivate.post("/messages/templates", { ...data, status: "draft" });
      return res.data;
    },
    onSuccess: () => {
      toast.success("Message saved as draft");
    },
    onError: () => {
      toast.error("Failed to save draft");
    },
  });

  const activateMutation = useMutation({
    mutationFn: async (data: MessageFormValues) => {
      const res = await axiosPrivate.post("/messages/templates", { ...data, status: "active" });
      return res.data;
    },
    onSuccess: () => {
      toast.success("Message activated successfully");
    },
    onError: () => {
      toast.error("Failed to activate message");
    },
  });

  function handleSaveDraft() {
    const values = form.getValues();
    saveDraftMutation.mutate(values);
  }

  function handleActivate() {
    form.handleSubmit((data) => {
      activateMutation.mutate(data);
    })();
  }

  function handlePreview(role: ReceiverRole) {
    setPreviewRole(role);
    // Could open a modal or show preview
    const body = form.getValues("message_body");
    const previewText = body
      .replace("{{Name}}", "John Doe")
      .replace("{{Role}}", RECEIVER_ROLE_LABELS[role])
      .replace("{{Action}}", "completed orientation");
    toast.info(`Preview as ${RECEIVER_ROLE_LABELS[role]}:\n\n${previewText}`, { duration: 5000 });
  }

  const currentSectionIndex = SECTIONS.findIndex((s) => s.id === activeSection);

  function goToNextSection() {
    if (currentSectionIndex < SECTIONS.length - 1) {
      setActiveSection(SECTIONS[currentSectionIndex + 1].id);
    }
  }

  function goToPrevSection() {
    if (currentSectionIndex > 0) {
      setActiveSection(SECTIONS[currentSectionIndex - 1].id);
    }
  }

  return (
    <div className="space-y-6 max-w-6xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Create Message Template</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Define automated messages for role-based communication
        </p>
      </div>

      {/* Section Tabs */}
      <div className="border-b overflow-x-auto">
        <nav className="flex gap-1 overflow-x-auto pb-px">
          {SECTIONS.map((section) => {
            const Icon = section.icon;
            const isActive = activeSection === section.id;
            return (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={cn(
                  "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap",
                  isActive
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted"
                )}
              >
                <Icon size={18} />
                {section.label}
              </button>
            );
          })}
        </nav>
      </div>

      <Form {...form}>
        <form className="space-y-6">
          {/* Section Content */}
          <div className="bg-card border rounded-lg p-6 min-h-[400px]">
            {activeSection === "metadata" && <MetadataSection form={form} />}
            {activeSection === "targeting" && <TargetingSection form={form} />}
            {activeSection === "category" && <CategorySection form={form} />}
            {activeSection === "trigger" && <TriggerSection form={form} triggerType={triggerType} />}
            {activeSection === "content" && <ContentSection form={form} />}
            {activeSection === "delivery" && <DeliverySection form={form} />}
          </div>

          {/* Navigation & Actions */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t">
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={goToPrevSection}
                disabled={currentSectionIndex === 0}
              >
                <ChevronLeft size={16} className="mr-1" />
                Previous
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={goToNextSection}
                disabled={currentSectionIndex === SECTIONS.length - 1}
              >
                Next
                <ChevronRight size={16} className="ml-1" />
              </Button>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleSaveDraft}
                disabled={saveDraftMutation.isPending}
              >
                <Save size={16} className="mr-2" />
                Save Draft
              </Button>

              <Select onValueChange={(val) => handlePreview(val as ReceiverRole)}>
                <SelectTrigger className="w-[150px]">
                  <Eye size={16} className="mr-2" />
                  <SelectValue placeholder="Preview as..." />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(RECEIVER_ROLE_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                type="button"
                onClick={handleActivate}
                disabled={activateMutation.isPending}
              >
                <Send size={16} className="mr-2" />
                Activate
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}

// ============================================================================
// SECTION COMPONENTS
// ============================================================================

interface SectionProps {
  form: ReturnType<typeof useForm<MessageFormValues>>;
}

function MetadataSection({ form }: SectionProps) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold mb-1">Basic Metadata</h2>
        <p className="text-sm text-muted-foreground">
          Define the message title, status, and internal notes
        </p>
      </div>

      <div className="grid gap-6 sm:grid-cols-2 items-start">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem className="space-y-2">
              <FormLabel>
                Message Title <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input placeholder="e.g., New Joiner – Orientation Completed" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="status"
          render={({ field }) => (
            <FormItem className="space-y-2">
              <FormLabel>Message Status</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="version_notes"
          render={({ field }) => (
            <FormItem className="sm:col-span-2 space-y-2">
              <FormLabel>Version / Notes (optional)</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Internal tracking notes, version history, etc."
                  className="min-h-[100px] resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}

function TargetingSection({ form }: SectionProps) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold mb-1">Role & Membership Targeting</h2>
        <p className="text-sm text-muted-foreground">
          Define who can send and receive this message
        </p>
      </div>

      <div className="grid gap-6 sm:grid-cols-2 items-start">
        {/* Sender Role */}
        <FormField
          control={form.control}
          name="sender_role"
          render={({ field }) => (
            <FormItem className="space-y-2">
              <FormLabel>
                Sender Role <span className="text-destructive">*</span>
              </FormLabel>
              <div className="space-y-2">
                {(Object.entries(SENDER_ROLE_LABELS) as [SenderRole, string][]).map(
                  ([value, label]) => (
                    <label
                      key={value}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2.5 border rounded-md cursor-pointer transition-colors text-sm",
                        field.value === value
                          ? "border-primary bg-primary/5"
                          : "hover:bg-muted/50"
                      )}
                    >
                      <input
                        type="radio"
                        name="sender_role"
                        value={value}
                        checked={field.value === value}
                        onChange={() => field.onChange(value)}
                        className="w-4 h-4 text-primary accent-primary"
                      />
                      <span>{label}</span>
                    </label>
                  )
                )}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Receiver Role */}
        <FormField
          control={form.control}
          name="receiver_role"
          render={({ field }) => (
            <FormItem className="space-y-2">
              <FormLabel>
                Receiver Role <span className="text-destructive">*</span>
              </FormLabel>
              <div className="space-y-2">
                {(Object.entries(RECEIVER_ROLE_LABELS) as [ReceiverRole, string][]).map(
                  ([value, label]) => (
                    <label
                      key={value}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2.5 border rounded-md cursor-pointer transition-colors text-sm",
                        field.value === value
                          ? "border-primary bg-primary/5"
                          : "hover:bg-muted/50"
                      )}
                    >
                      <input
                        type="radio"
                        name="receiver_role"
                        value={value}
                        checked={field.value === value}
                        onChange={() => field.onChange(value)}
                        className="w-4 h-4 text-primary accent-primary"
                      />
                      <span>{label}</span>
                    </label>
                  )
                )}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Membership State */}
      <FormField
        control={form.control}
        name="membership_states"
        render={() => (
          <FormItem className="space-y-2">
            <FormLabel>Membership State (Optional)</FormLabel>
            <div className="grid gap-2 grid-cols-2 lg:grid-cols-4">
              {(Object.entries(MEMBERSHIP_STATE_LABELS) as [MembershipState, string][]).map(
                ([value, label]) => (
                  <FormField
                    key={value}
                    control={form.control}
                    name="membership_states"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-3 px-3 py-2.5 border rounded-md">
                        <FormControl>
                          <Checkbox
                            checked={field.value?.includes(value)}
                            onCheckedChange={(checked) => {
                              const newValue = checked
                                ? [...(field.value || []), value]
                                : field.value?.filter((v) => v !== value) || [];
                              field.onChange(newValue);
                            }}
                          />
                        </FormControl>
                        <FormLabel className="font-normal cursor-pointer">{label}</FormLabel>
                      </FormItem>
                    )}
                  />
                )
              )}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

function CategorySection({ form }: SectionProps) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold mb-1">Message Category</h2>
        <p className="text-sm text-muted-foreground">
          Categorize the message by subject matter
        </p>
      </div>

      {/* Primary Subject */}
      <FormField
        control={form.control}
        name="primary_subject"
        render={({ field }) => (
          <FormItem className="space-y-2">
            <FormLabel>
              Primary Subject <span className="text-destructive">*</span>
            </FormLabel>
            <div className="grid gap-2 grid-cols-2 lg:grid-cols-4">
              {(Object.entries(PRIMARY_SUBJECT_LABELS) as [PrimarySubject, string][]).map(
                ([value, label]) => (
                  <label
                    key={value}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2.5 border rounded-md cursor-pointer transition-colors",
                      field.value === value
                        ? "border-primary bg-primary/5"
                        : "hover:bg-muted/50"
                    )}
                  >
                    <input
                      type="radio"
                      name="primary_subject"
                      value={value}
                      checked={field.value === value}
                      onChange={() => field.onChange(value)}
                      className="w-4 h-4 text-primary accent-primary shrink-0"
                    />
                    <span className="text-sm">{label}</span>
                  </label>
                )
              )}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Secondary Tags */}
      <FormField
        control={form.control}
        name="secondary_tags"
        render={() => (
          <FormItem className="space-y-2">
            <FormLabel>Secondary Tags</FormLabel>
            <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
              {(Object.entries(SECONDARY_TAG_LABELS) as [SecondaryTag, string][]).map(
                ([value, label]) => (
                  <FormField
                    key={value}
                    control={form.control}
                    name="secondary_tags"
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2 px-3 py-2.5 border rounded-md">
                        <FormControl>
                          <Checkbox
                            checked={field.value?.includes(value)}
                            onCheckedChange={(checked) => {
                              const newValue = checked
                                ? [...(field.value || []), value]
                                : field.value?.filter((v) => v !== value) || [];
                              field.onChange(newValue);
                            }}
                          />
                        </FormControl>
                        <FormLabel className="font-normal cursor-pointer text-sm">
                          {label}
                        </FormLabel>
                      </FormItem>
                    )}
                  />
                )
              )}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

interface TriggerSectionProps extends SectionProps {
  triggerType: TriggerType;
}

function TriggerSection({ form, triggerType }: TriggerSectionProps) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold mb-1">Trigger Definition</h2>
        <p className="text-sm text-muted-foreground">
          Define when this message should be triggered
        </p>
      </div>

      {/* Trigger Type */}
      <FormField
        control={form.control}
        name="trigger_type"
        render={({ field }) => (
          <FormItem className="space-y-2">
            <FormLabel>
              Trigger Type <span className="text-destructive">*</span>
            </FormLabel>
            <div className="grid gap-2 grid-cols-3">
              {(Object.entries(TRIGGER_TYPE_LABELS) as [TriggerType, string][]).map(
                ([value, label]) => (
                  <label
                    key={value}
                    className={cn(
                      "flex items-center gap-3 px-3 py-3 border rounded-md cursor-pointer transition-colors",
                      field.value === value
                        ? "border-primary bg-primary/5"
                        : "hover:bg-muted/50"
                    )}
                  >
                    <input
                      type="radio"
                      name="trigger_type"
                      value={value}
                      checked={field.value === value}
                      onChange={() => {
                        field.onChange(value);
                        // Clear other trigger fields
                        form.setValue("event_trigger", undefined);
                        form.setValue("action_trigger", undefined);
                        form.setValue("time_trigger_days", undefined);
                      }}
                      className="w-4 h-4 text-primary accent-primary"
                    />
                    <span className="font-medium text-sm">{label}</span>
                  </label>
                )
              )}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Conditional Trigger Fields */}
      <div className="border-t pt-6 mt-6">
        {triggerType === "event" && (
          <FormField
            control={form.control}
            name="event_trigger"
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormLabel>Event Trigger</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="max-w-sm">
                      <SelectValue placeholder="Select an event..." />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {(Object.entries(EVENT_TRIGGER_LABELS) as [EventTrigger, string][]).map(
                      ([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
                <FormDescription>
                  Message triggers when this event occurs in the system
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {triggerType === "action" && (
          <FormField
            control={form.control}
            name="action_trigger"
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormLabel>Action Trigger</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="max-w-sm">
                      <SelectValue placeholder="Select a user action..." />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {(Object.entries(ACTION_TRIGGER_LABELS) as [ActionTrigger, string][]).map(
                      ([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
                <FormDescription>
                  Message triggers when the user performs this action
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {triggerType === "time" && (
          <FormField
            control={form.control}
            name="time_trigger_days"
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormLabel>Time Trigger (Days)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={1}
                    placeholder="Enter number of days"
                    className="max-w-[180px]"
                    value={field.value ?? ""}
                    onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                  />
                </FormControl>
                <FormDescription>
                  Number of days after joining / last activity / DXN membership
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>
    </div>
  );
}

function ContentSection({ form }: SectionProps) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold mb-1">Message Content</h2>
        <p className="text-sm text-muted-foreground">
          Write the message body and set the tone
        </p>
      </div>

      {/* Placeholders Help */}
      <div className="bg-muted/30 border rounded-md p-4">
        <h4 className="font-medium text-sm mb-2">Available Placeholders</h4>
        <div className="flex flex-wrap gap-3">
          {MESSAGE_PLACEHOLDERS.map((p) => (
            <div key={p.token} className="flex items-center gap-2">
              <code className="bg-background px-2 py-1 rounded text-xs font-mono border">
                {p.token}
              </code>
              <span className="text-xs text-muted-foreground">— {p.description}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Message Body */}
      <FormField
        control={form.control}
        name="message_body"
        render={({ field }) => (
          <FormItem className="space-y-2">
            <FormLabel>
              Message Body <span className="text-destructive">*</span>
            </FormLabel>
            <FormControl>
              <Textarea
                placeholder="Hi {{Name}}, welcome to the team! As a {{Role}}, you'll be..."
                className="min-h-[180px] font-mono text-sm resize-none"
                {...field}
              />
            </FormControl>
            <FormDescription className="text-xs">
              Plain text only (v1). Use placeholders for personalization.
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Tone Selector */}
      <FormField
        control={form.control}
        name="tone"
        render={({ field }) => (
          <FormItem className="space-y-2">
            <FormLabel>Tone</FormLabel>
            <div className="grid gap-2 grid-cols-2 lg:grid-cols-4">
              {(Object.entries(TONE_LABELS) as [ToneType, string][]).map(([value, label]) => (
                <label
                  key={value}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2.5 border rounded-md cursor-pointer transition-colors text-sm",
                    field.value === value
                      ? "border-primary bg-primary/5"
                      : "hover:bg-muted/50"
                  )}
                >
                  <input
                    type="radio"
                    name="tone"
                    value={value}
                    checked={field.value === value}
                    onChange={() => field.onChange(value)}
                    className="w-4 h-4 text-primary accent-primary"
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

function DeliverySection({ form }: SectionProps) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold mb-1">Delivery Rules (Guardrails)</h2>
        <p className="text-sm text-muted-foreground">
          Configure how and when messages are delivered
        </p>
      </div>

      <div className="grid gap-6 sm:grid-cols-2 items-start">
        {/* Send Direction */}
        <FormField
          control={form.control}
          name="send_direction"
          render={({ field }) => (
            <FormItem className="space-y-2">
              <FormLabel>Send Direction</FormLabel>
              <div className="space-y-2">
                {(Object.entries(SEND_DIRECTION_LABELS) as [SendDirection, string][]).map(
                  ([value, label]) => (
                    <label
                      key={value}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2.5 border rounded-md cursor-pointer transition-colors text-sm",
                        field.value === value
                          ? "border-primary bg-primary/5"
                          : "hover:bg-muted/50"
                      )}
                    >
                      <input
                        type="radio"
                        name="send_direction"
                        value={value}
                        checked={field.value === value}
                        onChange={() => field.onChange(value)}
                        className="w-4 h-4 text-primary accent-primary"
                      />
                      <span>{label}</span>
                    </label>
                  )
                )}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Frequency Limit */}
        <FormField
          control={form.control}
          name="frequency_limit"
          render={({ field }) => (
            <FormItem className="space-y-2">
              <FormLabel>Frequency Limit</FormLabel>
              <div className="space-y-2">
                {(Object.entries(FREQUENCY_LIMIT_LABELS) as [FrequencyLimit, string][]).map(
                  ([value, label]) => (
                    <label
                      key={value}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2.5 border rounded-md cursor-pointer transition-colors text-sm",
                        field.value === value
                          ? "border-primary bg-primary/5"
                          : "hover:bg-muted/50"
                      )}
                    >
                      <input
                        type="radio"
                        name="frequency_limit"
                        value={value}
                        checked={field.value === value}
                        onChange={() => field.onChange(value)}
                        className="w-4 h-4 text-primary accent-primary"
                      />
                      <span>{label}</span>
                    </label>
                  )
                )}
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Visibility */}
      <FormField
        control={form.control}
        name="visibility"
        render={({ field }) => (
          <FormItem className="space-y-2">
            <FormLabel>Visibility</FormLabel>
            <div className="grid gap-2 grid-cols-2">
              {(Object.entries(VISIBILITY_LABELS) as [VisibilityType, string][]).map(
                ([value, label]) => (
                  <label
                    key={value}
                    className={cn(
                      "flex items-center gap-3 px-3 py-3 border rounded-md cursor-pointer transition-colors text-sm",
                      field.value === value
                        ? "border-primary bg-primary/5"
                        : "hover:bg-muted/50"
                    )}
                  >
                    <input
                      type="radio"
                      name="visibility"
                      value={value}
                      checked={field.value === value}
                      onChange={() => field.onChange(value)}
                      className="w-4 h-4 text-primary accent-primary"
                    />
                    <span>{label}</span>
                  </label>
                )
              )}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
